"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { assignTask, contactName } from "@/lib/breach/assignments"
import type { Workspace } from "@/lib/breach/workspace"
import type { WorkspaceUpdater } from "./incident-tools"

const NEW = "__new__"
const field = "w-full rounded-md border border-border bg-background p-2 text-sm"

/** Hand a task to someone in Contacts (or add them on the spot). The assignment and its time go on the Timeline. */
export function AssignForm({ w, update, defaults, onClose }: {
  w: Workspace
  update: WorkspaceUpdater
  defaults?: { title?: string; itemId?: string; contactId?: string }
  onClose: () => void
}) {
  const [title, setTitle] = useState(defaults?.title ?? "")
  const [who, setWho] = useState(defaults?.contactId ?? (w.contacts[0]?.id ?? NEW))
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [due, setDue] = useState("")
  const [note, setNote] = useState("")
  const isNew = who === NEW
  const ok = title.trim() && (isNew ? name.trim() : who)

  return (
    <form className="grid gap-2 rounded-md border border-border bg-background/60 p-3 print:hidden" aria-label="Assign a task"
      onSubmit={(e) => {
        e.preventDefault(); if (!ok) return
        update((x) => assignTask(x, {
          title, itemId: defaults?.itemId, note,
          contactId: isNew ? null : who,
          newPerson: isNew ? { name, phone } : undefined,
          dueAt: due ? new Date(due).toISOString() : undefined,
        }))
        onClose()
      }}>
      <label className="grid gap-1 text-xs">What needs doing
        <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="For example: Remotely wipe the lost laptop" />
      </label>
      <label className="grid gap-1 text-xs">Who
        <select className={field} value={who} onChange={(e) => setWho(e.target.value)}>
          {w.contacts.map((c) => <option key={c.id} value={c.id}>{contactName(c)}{c.role && c.name ? ` (${c.role})` : ""}</option>)}
          <option value={NEW}>Someone new…</option>
        </select>
      </label>
      {isNew && (
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={field} aria-label="Name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className={field} aria-label="Phone" placeholder="Phone (optional)" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      )}
      <label className="grid gap-1 text-xs">Needed by (optional)
        <input type="datetime-local" className={field} value={due} onChange={(e) => setDue(e.target.value)} />
      </label>
      <label className="grid gap-1 text-xs">Note for them (optional)
        <input className={field} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything they need to know" />
      </label>
      <p className="text-xs text-muted-foreground">Don&apos;t put passwords or incident details in the note. You&apos;ll record when it&apos;s done, and it goes on your Timeline.</p>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!ok}>Assign</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  )
}
